// Pure helpers for GTC trees: ordering + amendment summary (fe#123 §4).

const toTime = value => {
  const t = value ? Date.parse(value) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

// Sort by sortOrder, then createdDate (same rule BE uses). Non-mutating.
export const sortByOrder = (list = []) =>
  [...(list || [])].sort(
    (a, b) =>
      (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0) ||
      toTime(a?.createdDate) - toTime(b?.createdDate)
  );

// Default sortOrder suggested for a new sibling: one past the highest.
export const nextSortOrder = (siblings = []) =>
  (siblings || []).reduce(
    (max, s) => Math.max(max, Number(s?.sortOrder) || 0),
    0
  ) + 1;

// dd/mm/yyyy hh:mm (local time), '' when empty/invalid.
export const formatAmendDate = value => {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const articleLabel = article =>
  ['Article', article?.number, article?.title].filter(Boolean).join(' ');

export const clauseLabel = clause =>
  `Clause ${clause?.clauseNumber ?? ''}`.trim();

// gtcs: tree(s) with amendment fields on each clause (isAmend).
// -> { total, totalClauses, perGtc, positions } in tree order.
export const buildAmendSummary = (gtcs = []) => {
  const perGtc = [];
  const positions = [];
  let total = 0;
  let totalClauses = 0;

  sortByOrder(gtcs || []).forEach(gtc => {
    let gtcTotal = 0;
    let gtcClauses = 0;
    sortByOrder(gtc?.categories).forEach(category => {
      sortByOrder(category?.articles).forEach(article => {
        sortByOrder(article?.clauses).forEach(clause => {
          gtcClauses += 1;
          if (!clause?.isAmend) return;
          gtcTotal += 1;
          positions.push({
            gtcId: gtc.id,
            categoryId: category.id,
            articleId: article.id,
            clauseId: clause.id,
            path: [
              gtc.gtcNumber,
              category.name,
              articleLabel(article),
              clauseLabel(clause),
            ]
              .filter(Boolean)
              .join(' › '),
          });
        });
      });
    });
    perGtc.push({
      gtcId: gtc?.id,
      gtcNumber: gtc?.gtcNumber,
      total: gtcTotal,
      totalClauses: gtcClauses,
    });
    total += gtcTotal;
    totalClauses += gtcClauses;
  });

  return { total, totalClauses, perGtc, positions };
};

// Amended clause count under any node (gtc / category / article / clause).
export const countAmended = node => {
  if (!node) return 0;
  if (Array.isArray(node.categories))
    return node.categories.reduce((n, c) => n + countAmended(c), 0);
  if (Array.isArray(node.articles))
    return node.articles.reduce((n, a) => n + countAmended(a), 0);
  if (Array.isArray(node.clauses))
    return node.clauses.reduce((n, c) => n + (c?.isAmend ? 1 : 0), 0);
  return 0;
};
